import React, { useState, useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOrgInstitution } from '../contexts/OrgInstitutionContext';
import './Classes.css';
import { 
  School as SchoolIcon, 
  Add as AddIcon, 
  Search as SearchIcon, 
  Sort as SortIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  Room as RoomIcon,
  CloudDownload as ImportIcon,
  Forum as ForumIcon,
  Person as PersonIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import Header from './Header';
import GoogleClassroomImport from './GoogleClassroomImport';

interface ClassData {
  id: number;
  name: string;
  subject: string | null;
  grade_level: string | null;
  description: string | null;
  room_number: string | null;
  period: string | null;
  start_time: string | null;
  end_time: string | null;
  days_of_week: string | null;
  academic_year: string | null;
  semester: string | null;
  student_count: number;
  max_enrollment: number | null;
  is_active: boolean;
  is_archived: boolean;
  google_classroom_id: string | null;
  created_at: string;
  updated_at: string;
  organization_id?: number;
  institution_id?: number;
  teacher?: {
    id: number;
    name: string;
    email: string;
  };
  students?: Student[];
  student_ids?: number[];
}

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  grade_level: string | null;
  email: string | null;
  student_id: string | null;
  is_active: boolean;
}

interface ClassFormData {
  name: string;
  subject: string;
  grade_level: string;
  description: string;
  room_number: string;
  period: string;
  start_time: string;
  end_time: string;
  days_of_week: string;
  academic_year: string;
  semester: string;
  max_enrollment: string;
}

interface AccessibleInstitution {
  id: number;
  name: string;
  slug: string;
  organization_id: number;
  organization_name?: string;
}

const Classes: React.FC = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useAuth();
  const { selectedOrganizationId, selectedInstitutionId } = useOrgInstitution();
  const { t } = useTranslation();
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingClass, setDeletingClass] = useState<ClassData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [selectedClass, setSelectedClass] = useState<ClassData | null>(null);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [availableStudents, setAvailableStudents] = useState<Student[]>([]);
  const [isGoogleClassroomImportOpen, setIsGoogleClassroomImportOpen] = useState(false);
  const [accessibleInstitutions, setAccessibleInstitutions] = useState<AccessibleInstitution[]>([]);
  const [institutionsLoading, setInstitutionsLoading] = useState(false);
  const [institutionsError, setInstitutionsError] = useState<string | null>(null);
  const [selectedInstitutionForForm, setSelectedInstitutionForForm] = useState<string>('');
  
  // Form state
  const [formData, setFormData] = useState<ClassFormData>({
    name: '',
    subject: '',
    grade_level: '',
    description: '',
    room_number: '',
    period: '',
    start_time: '',
    end_time: '',
    days_of_week: '',
    academic_year: '',
    semester: '',
    max_enrollment: ''
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Stats
  const stats = {
    total_classes: classes.filter(c => !c.is_archived).length,
    total_students: classes.reduce((sum, c) => sum + c.student_count, 0),
    active_classes: classes.filter(c => c.is_active && !c.is_archived).length,
    archived_classes: classes.filter(c => c.is_archived).length
  };

  // API call to fetch classes
  const fetchClasses = async () => {
    if (!token || !isAuthenticated) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Build query params with org/institution filters
      const queryParams = new URLSearchParams({
        include_students: 'true'
      });

      // Add filters from context
      if (selectedOrganizationId) {
        queryParams.append('organization_id', selectedOrganizationId.toString());
      }
      if (selectedInstitutionId) {
        queryParams.append('institution_id', selectedInstitutionId.toString());
      }

      const response = await fetch(`/api/classes?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setClasses(data.classes || []);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to fetch classes: ${errorMessage}`);
      console.error('Fetch classes error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch classes on component mount and auth state changes
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchClasses();
    }
  }, [token, isAuthenticated, authLoading]);

  // Refetch when organization/institution selection changes
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchClasses();
    }
  }, [selectedOrganizationId, selectedInstitutionId]);

  // Reset form data
  const resetFormData = () => {
    setFormData({
      name: '',
      subject: '',
      grade_level: '',
      description: '',
      room_number: '',
      period: '',
      start_time: '',
      end_time: '',
      days_of_week: '',
      academic_year: '',
      semester: '',
      max_enrollment: ''
    });
    setSelectedInstitutionForForm('');
    setInstitutionsError(null);
    setFormErrors([]);
  };

  const fetchAccessibleInstitutions = async (): Promise<AccessibleInstitution[]> => {
    if (!token || !isAuthenticated) return [];
    setInstitutionsLoading(true);
    setInstitutionsError(null);
    try {
      const response = await fetch('/api/institutions/accessible?status=active', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const institutions = (data.institutions || []) as AccessibleInstitution[];
      setAccessibleInstitutions(institutions);
      return institutions;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setInstitutionsError(errorMessage);
      setAccessibleInstitutions([]);
      return [];
    } finally {
      setInstitutionsLoading(false);
    }
  };

  // Load institutions when the add/edit modal opens
  useEffect(() => {
    const modalOpen = isAddModalOpen || isEditModalOpen;
    if (!modalOpen) return;
    if (!token || !isAuthenticated) return;

    (async () => {
      const institutions = await fetchAccessibleInstitutions();

      // Choose a sensible default for the dropdown
      const preferredId =
        editingClass?.institution_id ??
        selectedInstitutionId ??
        (institutions.length === 1 ? institutions[0].id : null);

      if (preferredId && institutions.some(i => i.id === preferredId)) {
        setSelectedInstitutionForForm(preferredId.toString());
      }
    })();
  }, [isAddModalOpen, isEditModalOpen, token, isAuthenticated, editingClass, selectedInstitutionId]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setFormSubmitting(true);
    setFormErrors([]);

    try {
      // Institution is required for creating a new class
      if (!editingClass && !selectedInstitutionForForm) {
        setFormErrors([t('classes.form.institutionRequired')]);
        return;
      }

      const submitData: any = { ...formData };
      
      // Convert empty strings to null for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });

      // Convert max_enrollment to number if provided
      if (submitData.max_enrollment) {
        submitData.max_enrollment = parseInt(submitData.max_enrollment);
      }

      if (selectedInstitutionForForm) {
        submitData.institution_id = parseInt(selectedInstitutionForForm);
      }

      const url = editingClass ? `/api/classes/${editingClass.id}` : '/api/classes';
      const method = editingClass ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          setFormErrors(Array.isArray(data.errors) ? data.errors : [data.errors]);
        } else {
          setFormErrors([data.error || 'Failed to save class']);
        }
        return;
      }

      // Success - refresh the classes list
      await fetchClasses();
      
      // Close modal and reset form
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      setEditingClass(null);
      resetFormData();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setFormErrors([`Failed to save class: ${errorMessage}`]);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deletingClass || !token) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/classes/${deletingClass.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete class');
      }

      // Success - refresh the classes list
      await fetchClasses();
      setIsDeleteModalOpen(false);
      setDeletingClass(null);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to delete class: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch available students for enrollment
  const fetchAvailableStudents = async (classId: number) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/classes/${classId}/available-students`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available students');
      }

      const data = await response.json();
      setAvailableStudents(data.students || []);
    } catch (err) {
      console.error('Error fetching available students:', err);
      setError('Failed to fetch available students');
    }
  };

  // Handle student enrollment
  const handleEnrollStudent = async (studentId: number) => {
    if (!selectedClass || !token) return;

    try {
      const response = await fetch(`/api/classes/${selectedClass.id}/students/${studentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to enroll student');
      }

      // Refresh classes and available students
      await fetchClasses();
      await fetchAvailableStudents(selectedClass.id);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to enroll student: ${errorMessage}`);
    }
  };

  // Handle student unenrollment
  const handleUnenrollStudent = async (studentId: number) => {
    if (!selectedClass || !token) return;

    try {
      const response = await fetch(`/api/classes/${selectedClass.id}/students/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to remove student');
      }

      // Refresh classes
      await fetchClasses();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to remove student: ${errorMessage}`);
    }
  };

  // Edit handler
  const handleEdit = (classData: ClassData) => {
    setEditingClass(classData);
    setFormData({
      name: classData.name,
      subject: classData.subject || '',
      grade_level: classData.grade_level || '',
      description: classData.description || '',
      room_number: classData.room_number || '',
      period: classData.period || '',
      start_time: classData.start_time || '',
      end_time: classData.end_time || '',
      days_of_week: classData.days_of_week || '',
      academic_year: classData.academic_year || '',
      semester: classData.semester || '',
      max_enrollment: classData.max_enrollment?.toString() || ''
    });
    setSelectedInstitutionForForm(classData.institution_id ? classData.institution_id.toString() : '');
    setIsEditModalOpen(true);
  };

  // Filter and sort classes
  const filteredClasses = classes
    .filter(classData => {
      const matchesSearch = searchTerm === '' || 
        classData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (classData.subject && classData.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (classData.room_number && classData.room_number.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesSearch;
    })
    .sort((a, b) => {
      let aVal = a[sortBy as keyof ClassData];
      let bVal = b[sortBy as keyof ClassData];
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortOrder === 'asc' ? 1 : -1;
      if (bVal == null) return sortOrder === 'asc' ? -1 : 1;
      
      // Convert strings to lowercase for case-insensitive sorting
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  // Helper function to get subject color class
  const getSubjectColor = (subject: string): string => {
    const subjectLower = subject.toLowerCase();
    if (subjectLower.includes('math')) return 'subject-math';
    if (subjectLower.includes('ela') || subjectLower.includes('english') || subjectLower.includes('language')) return 'subject-ela';
    if (subjectLower.includes('science')) return 'subject-science';
    if (subjectLower.includes('social') || subjectLower.includes('history')) return 'subject-social';
    if (subjectLower.includes('art')) return 'subject-art';
    if (subjectLower.includes('music')) return 'subject-music';
    if (subjectLower.includes('pe') || subjectLower.includes('physical')) return 'subject-pe';
    return 'subject-default';
  };

  if (authLoading) {
    return (
      <div className="classes-page">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="classes-section">
            <div className="loading-state">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="classes-page">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="classes-section">
            <div className="error-state">Please log in to view classes.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="classes-page">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="classes-section">
          
          {/* Header */}
          <div className="classes-header">
            <div className="page-header-content">
              <h1>My Classes</h1>
              <p>Manage your classroom sections and student enrollment</p>
            </div>
            <div className="header-actions">
              <button 
                className="btn-secondary"
                onClick={() => setIsGoogleClassroomImportOpen(true)}
                title="Import classes from Google Classroom"
              >
                <ImportIcon />
                Import from Google Classroom
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  resetFormData();
                  setIsAddModalOpen(true);
                }}
              >
                <AddIcon />
                {t('classes.addClass')}
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-cards">
            <div className="stat-card">
              <h3 className="stat-value">{stats.total_classes}</h3>
              <p className="stat-label">Total Classes</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-value">{stats.total_students}</h3>
              <p className="stat-label">Total Students</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-value">{stats.active_classes}</h3>
              <p className="stat-label">Active Classes</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-value">{stats.archived_classes}</h3>
              <p className="stat-label">Archived</p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Controls */}
          <div className="controls-bar">
            <div className="search-container">
              <SearchIcon className="search-icon" />
              <input
                type="text"
                placeholder={t('classes.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                aria-label={t('classes.searchPlaceholder')}
              />
            </div>
            <div className="controls-right">
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
                aria-label={t('classes.sort.sortBy')}
              >
                <option value="name">{t('classes.sort.sortByName')}</option>
                <option value="subject">{t('classes.sort.subject')}</option>
                <option value="grade_level">{t('classes.sort.grade')}</option>
                <option value="student_count">{t('classes.sort.studentCount')}</option>
                <option value="created_at">{t('classes.sort.createdDate')}</option>
              </select>
              <div className="view-toggle" role="group" aria-label={t('classes.view.toggleLabel')}>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  title={t('classes.view.gridView')}
                  aria-label={t('classes.view.gridView')}
                  aria-pressed={viewMode === 'grid'}
                >
                  <GridViewIcon />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  title={t('classes.view.listView')}
                  aria-label={t('classes.view.listView')}
                  aria-pressed={viewMode === 'list'}
                >
                  <ViewListIcon />
                </button>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading classes...</p>
              </div>
            </div>
          )}

          {/* Classes Grid/List */}
          {!loading && (
            <>
              {filteredClasses.length === 0 ? (
                <div className="empty-state">
                  <SchoolIcon className="empty-icon" />
                  <h3>{t('classes.noClassesFound')}</h3>
                  <p>
                    {searchTerm 
                      ? t('classes.noClassesMatch', { searchTerm })
                      : classes.length === 0 
                        ? t('classes.noClassesExist')
                        : t('classes.tryAdjusting')
                    }
                  </p>
                  {classes.length === 0 && (
                    <button 
                      className="btn-primary"
                      onClick={() => {
                        resetFormData();
                        setIsAddModalOpen(true);
                      }}
                    >
                      <AddIcon />
                      {t('classes.createFirstClass')}
                    </button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="classes-grid">
                  {filteredClasses.map((classData) => (
                    <div key={classData.id} className={`class-card ${classData.is_archived ? 'archived' : ''}`}>
                      <div className="class-card-header">
                        <div className="class-info">
                          <h3 className="class-name">{classData.name}</h3>
                          {classData.subject && (
                            <span className="class-subject">{classData.subject}</span>
                          )}
                        </div>
                        <div className="class-actions">
                          <button
                            onClick={() => navigate(`/classes/${classData.id}/feed`)}
                            className="btn-icon btn-feed"
                            title={t('classes.actions.viewFeed')}
                            aria-label={t('classes.actions.viewFeed')}
                          >
                            <ForumIcon />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedClass(classData);
                              fetchAvailableStudents(classData.id);
                              setIsEnrollModalOpen(true);
                            }}
                            className="btn-icon"
                            title={t('classes.actions.manageStudents')}
                            aria-label={t('classes.actions.manageStudents')}
                          >
                            <PersonAddIcon />
                          </button>
                          <button
                            onClick={() => handleEdit(classData)}
                            className="btn-icon"
                            title={t('classes.actions.editClass')}
                            aria-label={t('classes.actions.editClass')}
                          >
                            <EditIcon />
                          </button>
                          <button
                            onClick={() => {
                              setDeletingClass(classData);
                              setIsDeleteModalOpen(true);
                            }}
                            className="btn-icon btn-danger"
                            title={t('classes.actions.deleteClass')}
                            aria-label={t('classes.actions.deleteClass')}
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      </div>

                      <div className="class-details">
                        {classData.teacher && (
                          <div className="detail-item teacher-info">
                            <PersonIcon className="detail-icon" />
                            <span className="detail-value">
                              <strong>{t('classes.details.teacher')}:</strong> {classData.teacher.name}
                            </span>
                          </div>
                        )}
                        {!classData.teacher && (
                          <div className="detail-item teacher-info warning">
                            <PersonIcon className="detail-icon" />
                            <span className="detail-value">
                              <strong>{t('classes.details.teacher')}:</strong> {t('classes.details.notAssigned')}
                            </span>
                          </div>
                        )}
                        {classData.grade_level && (
                          <div className="detail-item">
                            <span className="detail-label">{t('classes.details.grade')}</span>
                            <span className="detail-value">{classData.grade_level}</span>
                          </div>
                        )}
                        {classData.room_number && (
                          <div className="detail-item">
                            <RoomIcon className="detail-icon" />
                            <span className="detail-value">{classData.room_number}</span>
                          </div>
                        )}
                        {classData.period && (
                          <div className="detail-item">
                            <ScheduleIcon className="detail-icon" />
                            <span className="detail-value">{classData.period}</span>
                          </div>
                        )}
                        {(classData.start_time && classData.end_time) && (
                          <div className="detail-item">
                            <span className="detail-value">
                              {classData.start_time} - {classData.end_time}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="class-enrollment">
                        <div className="enrollment-info">
                          <PeopleIcon className="enrollment-icon" />
                          <span className="enrollment-count">
                            {t('classes.details.studentCount', { count: classData.student_count })}
                            {classData.max_enrollment && ` / ${classData.max_enrollment}`}
                          </span>
                        </div>
                        {classData.student_count > 0 && (
                          <div className="enrolled-students">
                            {classData.students?.slice(0, 3).map((student) => (
                              <span key={student.id} className="student-chip">
                                {student.first_name} {student.last_name}
                              </span>
                            ))}
                            {classData.student_count > 3 && (
                              <span className="more-students">
                                {t('classes.details.moreStudents', { count: classData.student_count - 3 })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {classData.description && (
                        <div className="class-description">
                          <p>{classData.description}</p>
                        </div>
                      )}

                      <div className="class-card-footer">
                        <div className="class-status">
                          <span className={`status-badge ${classData.is_active ? 'active' : 'inactive'}`}>
                            {classData.is_active ? t('classes.details.active') : t('classes.details.inactive')}
                          </span>
                          {classData.is_archived && (
                            <span className="status-badge archived">{t('classes.details.archived')}</span>
                          )}
                          {classData.google_classroom_id ? (
                            <span className="status-badge google-classroom" title={t('classes.details.syncedTooltip')}>
                              <ImportIcon style={{ fontSize: '14px', marginRight: '4px' }} />
                              {t('classes.details.googleClassroomSynced')}
                            </span>
                          ) : (
                            <span className="status-badge standalone" title={t('classes.details.standaloneTooltip')}>
                              {t('classes.details.standalone')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* List View */
                <div className="classes-list">
                  {filteredClasses.map((classData) => (
                    <div 
                      key={classData.id} 
                      className={`class-list-item ${classData.is_archived ? 'archived' : ''}`}
                      onClick={() => navigate(`/classes/${classData.id}/feed`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/classes/${classData.id}/feed`);
                        }
                      }}
                      aria-label={`${classData.name} - ${t('classes.actions.viewFeed')}`}
                    >
                      <div className="list-item-main">
                        <div className="list-item-title">
                          <h3 className="list-class-name">{classData.name}</h3>
                          {classData.subject && (
                            <span className={`list-subject-badge ${getSubjectColor(classData.subject)}`}>
                              {classData.subject.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="list-item-details">
                          {classData.teacher && (
                            <span className="list-detail-item">
                              <PersonIcon className="list-detail-icon" />
                              {classData.teacher.name}
                            </span>
                          )}
                          <span className="list-detail-item">
                            <PeopleIcon className="list-detail-icon" />
                            {classData.student_count}/{classData.max_enrollment || 25}
                          </span>
                          {classData.grade_level && (
                            <span className="list-detail-item">
                              {t('classes.details.gradeShort')} {classData.grade_level}
                            </span>
                          )}
                          {classData.room_number && (
                            <span className="list-detail-item">
                              <RoomIcon className="list-detail-icon" />
                              {classData.room_number}
                            </span>
                          )}
                          {classData.period && (
                            <span className="list-detail-item">
                              {classData.period}
                            </span>
                          )}
                          {(classData.start_time && classData.end_time) && (
                            <span className="list-detail-item">
                              <AccessTimeIcon className="list-detail-icon" />
                              {classData.start_time} - {classData.end_time}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="list-item-badges" onClick={(e) => e.stopPropagation()}>
                        <span className={`list-status-badge ${classData.is_active ? 'active' : 'inactive'}`}>
                          {classData.is_active ? t('classes.details.active').toUpperCase() : t('classes.details.inactive').toUpperCase()}
                        </span>
                        {classData.google_classroom_id ? null : (
                          <span className="list-status-badge standalone">
                            {t('classes.details.standalone').toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Class Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: true
          }}
        >
          <div className="modal-overlay" role="presentation">
            <div 
              className="modal-content"
              role="dialog"
              aria-modal="true"
              aria-labelledby="class-modal-title"
            >
              <div className="modal-header">
                <h2 id="class-modal-title">{editingClass ? t('classes.form.editClass') : t('classes.form.addNew')}</h2>
                <button 
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setIsEditModalOpen(false);
                    setEditingClass(null);
                    resetFormData();
                  }}
                  className="modal-close"
                  aria-label="Close dialog"
                >
                  ×
                </button>
              </div>

            <form onSubmit={handleSubmit} className="class-form">
              {/* Form Errors */}
              {formErrors.length > 0 && (
                <div className="form-errors">
                  {formErrors.map((error, index) => (
                    <p key={index}>{error}</p>
                  ))}
                </div>
              )}

              <div className="form-grid">
                {/* Basic Information */}
                <div className="form-section">
                  <h3>Basic Information</h3>

                  <div className="form-group">
                    <label htmlFor="institution_id">{t('classes.form.institution')} *</label>
                    <select
                      id="institution_id"
                      value={selectedInstitutionForForm}
                      onChange={(e) => setSelectedInstitutionForForm(e.target.value)}
                      required={!editingClass}
                      disabled={!!editingClass || institutionsLoading}
                      aria-label={t('classes.form.institutionAria')}
                    >
                      <option value="">{institutionsLoading ? t('classes.form.loadingInstitutions') : t('classes.form.selectInstitution')}</option>
                      {accessibleInstitutions.map((inst) => (
                        <option key={inst.id} value={inst.id}>
                          {inst.organization_name ? `${inst.organization_name} — ${inst.name}` : inst.name}
                        </option>
                      ))}
                    </select>
                    {institutionsError && (
                      <p className="field-hint">{t('classes.form.institutionsLoadFailed', { reason: institutionsError })}</p>
                    )}
                    {!institutionsLoading && !institutionsError && accessibleInstitutions.length === 0 && (
                      <p className="field-hint">{t('classes.form.noInstitutionsAvailable')}</p>
                    )}
                    {editingClass && (
                      <p className="field-hint">{t('classes.form.institutionEditLocked')}</p>
                    )}
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Class Name *</label>
                      <input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        placeholder="e.g., 5th Grade Math"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject</label>
                      <input
                        id="subject"
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        placeholder="e.g., Mathematics"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="grade_level">Grade Level</label>
                      <select
                        id="grade_level"
                        value={formData.grade_level}
                        onChange={(e) => setFormData({...formData, grade_level: e.target.value})}
                      >
                        <option value="">Select Grade</option>
                        <option value="Pre-K">Pre-K</option>
                        <option value="Kindergarten">Kindergarten</option>
                        <option value="1st">1st Grade</option>
                        <option value="2nd">2nd Grade</option>
                        <option value="3rd">3rd Grade</option>
                        <option value="4th">4th Grade</option>
                        <option value="5th">5th Grade</option>
                        <option value="6th">6th Grade</option>
                        <option value="7th">7th Grade</option>
                        <option value="8th">8th Grade</option>
                        <option value="9th">9th Grade</option>
                        <option value="10th">10th Grade</option>
                        <option value="11th">11th Grade</option>
                        <option value="12th">12th Grade</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="max_enrollment">Max Enrollment</label>
                      <input
                        id="max_enrollment"
                        type="number"
                        min="1"
                        max="100"
                        value={formData.max_enrollment}
                        onChange={(e) => setFormData({...formData, max_enrollment: e.target.value})}
                        placeholder="e.g., 25"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Brief description of the class"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Schedule & Location */}
                <div className="form-section">
                  <h3>Schedule & Location</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="room_number">Room Number</label>
                      <input
                        id="room_number"
                        type="text"
                        value={formData.room_number}
                        onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                        placeholder="e.g., Room 101"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="period">Period</label>
                      <input
                        id="period"
                        type="text"
                        value={formData.period}
                        onChange={(e) => setFormData({...formData, period: e.target.value})}
                        placeholder="e.g., 1st Period, Block A"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="start_time">Start Time</label>
                      <input
                        id="start_time"
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="end_time">End Time</label>
                      <input
                        id="end_time"
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="days_of_week">Days of Week</label>
                    <input
                      id="days_of_week"
                      type="text"
                      value={formData.days_of_week}
                      onChange={(e) => setFormData({...formData, days_of_week: e.target.value})}
                      placeholder="e.g., MWF, TR, MTWRF"
                    />
                  </div>
                </div>

                {/* Academic Year */}
                <div className="form-section">
                  <h3>Academic Period</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="academic_year">Academic Year</label>
                      <input
                        id="academic_year"
                        type="text"
                        value={formData.academic_year}
                        onChange={(e) => setFormData({...formData, academic_year: e.target.value})}
                        placeholder="e.g., 2023-2024"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="semester">Semester</label>
                      <select
                        id="semester"
                        value={formData.semester}
                        onChange={(e) => setFormData({...formData, semester: e.target.value})}
                      >
                        <option value="">Select Semester</option>
                        <option value="Fall">Fall</option>
                        <option value="Spring">Spring</option>
                        <option value="Summer">Summer</option>
                        <option value="Full Year">Full Year</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setIsEditModalOpen(false);
                    setEditingClass(null);
                    resetFormData();
                  }}
                  className="btn-secondary"
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={formSubmitting}
                >
                  {formSubmitting ? t('classes.form.saving') : (editingClass ? t('classes.form.updateClass') : t('classes.form.createClass'))}
                </button>
              </div>
            </form>
          </div>
        </div>
        </FocusTrap>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingClass && (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: true
          }}
        >
          <div className="modal-overlay" role="presentation">
            <div 
              className="modal-content delete-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-class-modal-title"
              aria-describedby="delete-class-modal-desc"
            >
              <div className="modal-header">
                <h2 id="delete-class-modal-title">Confirm Delete</h2>
                <button 
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingClass(null);
                  }}
                  className="modal-close"
                  aria-label="Close dialog"
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <p id="delete-class-modal-desc">Are you sure you want to delete the class:</p>
                <p className="delete-class-name">"{deletingClass.name}"</p>
                <p>This will also remove all student enrollments. This action cannot be undone.</p>
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingClass(null);
                  }}
                  className="btn-secondary"
                  disabled={isDeleting}
                  aria-label="Cancel delete"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="btn-danger"
                  disabled={isDeleting}
                  aria-label="Confirm delete class"
                >
                  {isDeleting ? t('classes.delete.deleting') : t('classes.delete.deleteClass')}
                </button>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}

      {/* Student Enrollment Modal */}
      {isEnrollModalOpen && selectedClass && (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: true
          }}
        >
          <div className="modal-overlay" role="presentation">
            <div 
              className="modal-content enrollment-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="enroll-modal-title"
            >
              <div className="modal-header">
                <h2 id="enroll-modal-title">Manage Students - {selectedClass.name}</h2>
                <button 
                  onClick={() => {
                    setIsEnrollModalOpen(false);
                    setSelectedClass(null);
                    setAvailableStudents([]);
                  }}
                  className="modal-close"
                  aria-label="Close dialog"
                >
                  ×
                </button>
              </div>

            <div className="enrollment-content">
              {/* Current Students */}
              <div className="enrollment-section">
                <h3>Current Students ({selectedClass.student_count})</h3>
                {selectedClass.students && selectedClass.students.length > 0 ? (
                  <div className="student-list">
                    {selectedClass.students.map((student) => (
                      <div key={student.id} className="student-item">
                        <div className="student-info">
                          <span className="student-name">{student.full_name}</span>
                          {student.grade_level && (
                            <span className="student-grade">Grade {student.grade_level}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnenrollStudent(student.id)}
                          className="btn-remove"
                          title="Remove from class"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-students">No students enrolled</p>
                )}
              </div>

              {/* Available Students */}
              <div className="enrollment-section">
                <h3>Available Students ({availableStudents.length})</h3>
                {availableStudents.length > 0 ? (
                  <div className="student-list">
                    {availableStudents.map((student) => (
                      <div key={student.id} className="student-item">
                        <div className="student-info">
                          <span className="student-name">{student.full_name}</span>
                          {student.grade_level && (
                            <span className="student-grade">Grade {student.grade_level}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleEnrollStudent(student.id)}
                          className="btn-add"
                          title="Add to class"
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </div>
                  ) : (
                    <p className="no-students">All your students are already enrolled in this class</p>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setIsEnrollModalOpen(false);
                    setSelectedClass(null);
                    setAvailableStudents([]);
                  }}
                  className="btn-secondary"
                  aria-label="Done enrolling students"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}

      {/* Google Classroom Import Modal */}
      <GoogleClassroomImport
        isOpen={isGoogleClassroomImportOpen}
        onClose={() => setIsGoogleClassroomImportOpen(false)}
        type="classes"
        onImportComplete={() => {
          // Refresh the classes list after import
          fetchClasses();
        }}
      />
    </div>
  );
};

export default Classes;
